import { differenceInDays } from 'date-fns/differenceInDays';
import { startOfQuarter } from 'date-fns/startOfQuarter';
import type { ChartGranularity, ChartSettingsStore } from '@/components/Filters/ChartSettings/Store/store';
import { getChartStartDate } from '@/components/Filters/ChartSettings/utils';
import type { Period } from '@/components/Filters/Period/store/store';
import type { StatisticsSeries } from '@/types/series';
import { fmt, now } from '@/utils/dates';
import type { StatisticsQuery } from '../modules/BaseQuery';
import type { Compare, Granularity } from '../types';
import { getComparison } from './getCompare';

const setCompare = <Series extends StatisticsSeries, C extends Compare>(
  clientQuery: StatisticsQuery<Series, C>,
  compareSlug: Period['compareSlug'],
) => {
  return getComparison({
    comparePeriod: compareSlug,
    granularity: clientQuery.period.granularity ?? null,
    end: clientQuery.period.end ?? null,
    start: clientQuery.period.start ?? null,
    window: clientQuery.period.window ?? null,
  }) as C;
};

const setChartGranularity = <Series extends StatisticsSeries, C extends Compare>(
  clientQuery: StatisticsQuery<Series, C>,
  chartGranularity: ChartGranularity,
) => {
  const chartGranularityConfig: Record<ChartGranularity, { granularity: Granularity; window: number }> = {
    week: { granularity: 'week', window: 1 },
    '4week': { granularity: 'week', window: 4 },
    month: { granularity: 'month', window: 1 },
    year: { granularity: 'year', window: 1 },
    quarter: { granularity: 'quarter', window: 1 },
  };

  const oldPeriod = clientQuery.period;
  return { ...oldPeriod, ...chartGranularityConfig[chartGranularity] };
};

type PeriodSlugNoCustom = Exclude<Period['periodSlug'], 'custom'>;
const setTablePeriod = <Series extends StatisticsSeries, C extends Compare>(
  clientQuery: StatisticsQuery<Series, C>,
  period: Period,
) => {
  const periodConfig: Record<PeriodSlugNoCustom, { granularity: Granularity; window?: number }> = {
    lastweek: { granularity: 'week', window: 1 },
    last4weeks: { granularity: 'week', window: 4 },
    last30days: { granularity: 'day', window: 30 },
    lastmonth: { granularity: 'month', window: 1 },
    lastquarter: { granularity: 'quarter', window: 1 },
    lasthalfyear: { granularity: 'half_year', window: 1 },
    lastyear: { granularity: 'year', window: 1 },
    currentquarter: (() => {
      const today = now();
      return { granularity: 'day', window: differenceInDays(today, startOfQuarter(today)) };
    })(),
    all: {
      granularity: null,
      window: 1,
    },
  };

  const oldPeriod = clientQuery.period;
  //TODO: fix with period start default
  if (period.periodSlug === 'all') {
    const newPeriod = {
      window: 1,
      granularity: null,
      start: '2013-01-01',
      end: fmt(now(), 'yyyy-MM-dd'),
    };

    return { ...newPeriod };
  }
  if (period.periodSlug === 'custom') {
    const newPeriod = {
      window: 1,
      granularity: null,
      start: period.start,
      end: period.end,
    };

    return { ...oldPeriod, ...newPeriod };
  }
  return { ...oldPeriod, ...periodConfig[period.periodSlug] };
};

const setChartPeriod = (chartSettings: ChartSettingsStore['chartSettings']) => {
  const chartStart = getChartStartDate(chartSettings.chartStart, chartSettings.chartGranularity, chartSettings.chart);
  return {
    start: chartStart,
    ...(!!chartSettings.chartEnd && { end: `${chartSettings.chartEnd}-12-31` }),
    window: chartSettings.chartGranularity === '4week' ? 4 : 1,
    granularity: chartSettings.chartGranularity === '4week' ? 'week' : chartSettings.chartGranularity,
  };
};

export { setChartGranularity, setChartPeriod, setCompare, setTablePeriod };
